import { useErrorHandler } from '~/hooks';
import { useSearchResult, useApplication } from '~/context';
import { Flex, Heading, Link, SrOnly, Table, Tag, Text, useToasts } from '@applyboard/crystal-ui';
import { Intake } from '~/data';
import { DocumentAlertHifiIcon } from '@applyboard/ui-icons';
import { SelectProgramButton } from './SelectProgramButton';
import { useChangeProgram } from '../hooks/useChangeProgram';
import React from 'react';
import { ProgramLength } from './ProgramLength';
import { ChangeProgramModal } from './ChangeProgramModal';

export const enumIntakeStatuses = [
  { code: 'OPEN', name: 'Open' },
  { code: 'WILL_OPEN', name: 'Opening Soon' },
  { code: 'CLOSED', name: 'Closed' },
];

export function ChangeProgramTable() {
  const { apiError } = useErrorHandler();
  const { application } = useApplication();
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedIntake, setSelectedIntake] = React.useState<Intake | null>(null);
  const { intakes, isFetchingIntakes, isIntakesError } = useSearchResult();
  const { changeProgram } = useChangeProgram(application.id);
  const toast = useToasts();

  if (isIntakesError) {
    apiError();
    return null;
  }

  function formatURL(url: string) {
    if (!url.startsWith('https://')) {
      return 'https://' + url;
    }
    return url;
  }

  return (
    <Flex direction="column">
      <div role="alert" aria-live="assertive">
        {isFetchingIntakes && <SrOnly>Loading intakes table</SrOnly>}
      </div>
      <ChangeProgramModal
        open={isOpen}
        onOpenChange={setIsOpen}
        intake={selectedIntake}
        onConfirm={async () => {
          try {
            if (selectedIntake) {
              await changeProgram(selectedIntake.id);
              toast.positive('Successfully updated application');
              window.location.href = `/applications/${application.id}`;
            }
          } catch (err) {
            apiError();
          }
        }}
      />
      <Table
        data={intakes}
        loading={isFetchingIntakes}
        overflow="scroll"
        columns={[
          {
            accessorKey: 'programName',
            header: 'Program Name',
            cell: (intake: Intake) => {
              return (
                <Flex direction="column" pt={2} pb={5} px={4}>
                  <Text id={intake.id}>
                    {intake.programUrl ? (
                      <Link
                        href={formatURL(intake.programUrl)}
                        mode="override_user_choice_and_open_in_new_tab"
                        variant="standalone"
                      >
                        {intake.programName}
                      </Link>
                    ) : (
                      intake.programName
                    )}
                  </Text>
                  <Flex gap={2} wrap>
                    {intake.programLevel ? <Tag intent="secondary">{intake.programLevel}</Tag> : null}
                    <ProgramLength
                      length={intake.programLength}
                      unit={intake.programLengthUnit as 'years' | 'months' | 'weeks'}
                    />
                  </Flex>
                </Flex>
              );
            },
          },
          {
            accessorKey: 'campus',
            header: 'Campus',
            cell: (intake: Intake) => {
              return <Table.TextCell>{intake.campusName}</Table.TextCell>;
            },
          },
          {
            accessorKey: 'start',
            header: 'Start',
            cell: (intake: Intake) => {
              return <Table.TextCell>{intake.intakeTermName}</Table.TextCell>;
            },
          },
          {
            accessorKey: 'status',
            header: 'Status',
            cell: (intake: Intake) => {
              return (
                <Table.TextCell>
                  <Tag intent={getIntakeStatusIntent(intake.status)}>{getIntakeStatus(intake.status)}</Tag>
                </Table.TextCell>
              );
            },
          },
          {
            accessorKey: 'id',
            header: 'Action',
            cell: (intake: Intake) => {
              return (
                <Table.TextCell>
                  <SelectProgramButton
                    intake={intake}
                    isDesktop={true}
                    onClick={async () => {
                      setSelectedIntake(intake);
                      setIsOpen(true);
                    }}
                  />
                </Table.TextCell>
              );
            },
          },
        ]}
      >
        <Table.NoData>
          <Flex align="center" justify="center" direction="column" gap={4} py={32}>
            <DocumentAlertHifiIcon />
            <Heading level={3}>No program intakes found.</Heading>
          </Flex>
        </Table.NoData>
      </Table>
    </Flex>
  );
}

function getIntakeStatus(intakeStatus: string): string {
  return enumIntakeStatuses.find((intake) => intake.code === intakeStatus)?.name as string;
}

function getIntakeStatusIntent(intakeStatus: string): 'positive' | 'negative' {
  return intakeStatus === 'OPEN' ? 'positive' : 'negative';
}
